<template>
  <dialog-scroll-lock width="300" :value="value" @input="$emit('input', false)">
    <v-card>
      <v-btn class="float-right ma-3" icon @click="$emit('input', false)">
        <v-icon v-html="mdiClose" />
      </v-btn>
      <v-card-title>Add to calendar</v-card-title>
      <v-card-subtitle class="pb-0">
        <template v-if="isValid">Select your calendar service</template>
        <span v-else class="warning--text">Event details are incomplete</span>
      </v-card-subtitle>
      <v-list class="text-body-2 primary--text">
        <v-list-item
          v-for="{ img, name, click } in vendors" :key="name"
          class="px-6 simple"
          :disabled="!isValid"
          @click="click"
        >
          <img width="24" height="24" :src="img" />
          <div>{{name}}</div>
        </v-list-item>
        <v-divider class="my-2" />
        <v-list-item class="px-6 simple" :disabled="!isValid" @click="download">
          <v-icon v-html="mdiFileDownload" />
          <div>
            <div>Other</div>
            <div class="text--label-active">Download calendar file</div>
          </div>
        </v-list-item>
      </v-list>
    </v-card>
  </dialog-scroll-lock>
</template>

<script>
import { format } from '@/utils/time.js';
import apple from '@/assets/apple.svg';
import googleCalendar from '@/assets/google-calendar.svg';
import microsoft365 from '@/assets/microsoft-365.svg';
import microsoftTeams from '@/assets/microsoft-teams.svg';
import outlook from '@/assets/outlook.svg';
import yahoo from '@/assets/yahoo.svg';
import { mdiAlert, mdiClose, mdiFileDownload } from '@mdi/js';


function formatICSDatetime(d) {
  // Formats an ISO datetime string for use with an iCal file (and with some services)
  return d.slice(0, 19).replace(/[-:]/g, '') + 'Z';
}


function formatICSText(t) {
  return (t || '').replace(/\\/gm, "\\\\").replace(/\r?\n/gm, "\\n").replace(/;/gm, "\\;").replace(/,/gm, "\\,");
}


export default {
  name: 'add-to-calendar-dialog',

  props: [ 'value', 'event' ],

  data() {
    return {
    };
  },

  computed: {
    isValid() {
      const e = this.event;
      return !!(e && e.name && e.from);
    },

    location() {
      // Most calendars only expect an address and break formatting if there is excess data like URLs
      const e = this.event;
      return e.location + (e.address ? ('\n' + e.address) : '');
    },

    details() {
      const e = this.event;
      let output = (e.details ? (e.details + '\n\n') : '') + e.location;
      if (e.address)
        output += '\n' + e.address;
      if (e.url_map)
        output += '\n\n' + e.url_map;
      if (e.url_virtual)
        output += '\n\n' + e.url_virtual;
      return output;
    },

    boundaries() {
      // Assume the event ends one hour after it starts if no 'to' datetime is provided
      const e = this.event;
      return [ new Date(e.from), new Date(e.to || (Date.parse(e.from) + 60 * 1000)) ];
    },

    vendors() {
      return [
        { img: apple, name: 'Apple', click: this.download },
        { img: googleCalendar, name: 'Google', click: this.openGoogle },
        // { img: microsoft365, name: 'Microsoft Office 365', click: this.openOffice365 },
        // { img: microsoftTeams, name: 'Microsoft Teams', click: this.openTeams },
        { img: outlook, name: 'Outlook', click: this.openOutlook },
        { img: yahoo, name: 'Yahoo', click: this.openYahoo }
      ];
    }
  },

  created() {
    Object.assign(this, { mdiAlert, mdiClose, mdiFileDownload });
  },

  methods: {
    // https://github.com/AnandChowdhary/calendar-link

    openGoogle() {
      // https://github.com/InteractionDesignFoundation/add-event-to-calendar-docs/blob/main/services/google.md
      const e = this.event;
      const params = {
        action: 'TEMPLATE',
        text: e.name,
        details: this.details,
        location: this.location.replace(/\n/g, ' '),
        dates: this.boundaries.map(x => formatICSDatetime(x.toISOString())).join('/'),
        ctz: e.tz
      };
      window.open(`https://calendar.google.com/calendar/render?${(new URLSearchParams(params)).toString()}`, '_blank');
    },

    // openOffice365() {

    // },

    openOutlook() {
      const params = {
        rru: 'addevent',
        startdt: this.boundaries[0].toISOString(),
        enddt: this.boundaries[1].toISOString(),
        subject: this.event.name,
        body: this.details,
        location: this.location
      };
      window.open(
        `https://outlook.office.com/calendar/action/compose?${(new URLSearchParams(params)).toString()}`,
        '_blank'
      );
    },

    // openMicrosoftTeams() {
    //   const params = {
    //     subject: this.event.name,
    //     content: this.event.details,
    //     startTime: this.boundaries[0].toISOString(),
    //     endTime: this.boundaries[1].toISOString()
    //   };
    //   window.open(`https://teams.microsoft.com/l/meeting/new?${(new URLSearchParams(params)).toString()}`, '_blank');
    // },

    openYahoo() {
      const params = {
        v: 60,
        title: this.event.name,
        st: this.boundaries[0].toISOString(),
        et: this.boundaries[1].toISOString(),
        desc: this.details,
        in_loc: this.location
      };
      window.open(`https://calendar.yahoo.com/?${(new URLSearchParams(params)).toString()}`, '_blank');
    },

    download() {
      const e = this.event;
      const now = formatICSDatetime((new Date()).toISOString());
      const [ start, end ] = this.boundaries.map(x => formatICSDatetime(format(x, 'iso', e.tz)).slice(0, -1));

      // Warning: this is potentially fragile as whatever is parsing the ICS file must know about the TZID. It is
      // possible (but complicated) to embed the timezone information directly into the ICS file, but fuck that. You
      // can use the following which is correct, but results in a worse user experience (this is what Paperless Post
      // does):
      // DTSTART;VALUE=DATE-TIME:${start}
      // DTEND;VALUE=DATE-TIME:${end}

      // TODO: it would be really nice to include a URL with the token that leads back to the invitation itself

      const ics = `BEGIN:VCALENDAR
PRODID:-//Ellacard//ellacard.com//EN
VERSION:2.0
CALSCALE:GREGORIAN
BEGIN:VEVENT
DTSTART;TZID=${e.tz}:${start}
DTEND;TZID=${e.tz}:${end}
DTSTAMP:${now}
CLASS:PRIVATE
CREATED:${now}
SUMMARY:${formatICSText(e.name)}
DESCRIPTION:${formatICSText(this.details)}
LAST-MODIFIED:${now}
LOCATION:${formatICSText(this.location)}
SEQUENCE:0
STATUS:TENTATIVE
TRANSP:OPAQUE
END:VEVENT
END:VCALENDAR`;

      const file = new File([ ics ], 'event.ics', { type: 'text/calendar' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(file);
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    }
  }
}
</script>
